<template>
  <div class="venue-map-canvas" ref="containerRef">
    <svg
      v-if="layout.imageWidth && layout.imageHeight"
      :viewBox="`0 0 ${layout.imageWidth} ${layout.imageHeight}`"
      class="map-svg"
      @click="onCanvasClick"
      @mousemove="onMouseMove"
    >
      <image
        v-if="layout.backgroundUrl"
        :href="layout.backgroundUrl"
        x="0"
        y="0"
        :width="layout.imageWidth"
        :height="layout.imageHeight"
        preserveAspectRatio="xMidYMid meet"
      />
      <rect
        v-else
        x="0"
        y="0"
        :width="layout.imageWidth"
        :height="layout.imageHeight"
        fill="#e8ecf0"
      />

      <g v-for="region in layout.regions" :key="region.id">
        <polygon
          :points="regionPoints(region)"
          :fill="regionFill(region)"
          :stroke="regionStroke(region)"
          stroke-width="3"
          class="region-polygon"
          @click.stop="onRegionClick(region)"
        />
        <text
          :x="labelPos(region)[0]"
          :y="labelPos(region)[1]"
          text-anchor="middle"
          class="region-label"
          pointer-events="none"
        >
          <tspan :x="labelPos(region)[0]" dy="-0.4em">{{ regionDisplayName(region) }}</tspan>
          <tspan
            v-if="showCounts && counts[region.id] != null"
            :x="labelPos(region)[0]"
            dy="1.2em"
            class="count-line"
          >{{ counts[region.id] }}人</tspan>
        </text>
      </g>

      <!-- 编辑模式：正在绘制的多边形 -->
      <g v-if="editable && draftPoints.length">
        <polyline
          :points="draftPointsSvg"
          fill="none"
          stroke="#1677ff"
          stroke-width="3"
          stroke-dasharray="8 4"
        />
        <circle
          v-for="(pt, idx) in draftPoints"
          :key="'d-' + idx"
          :cx="pt[0]"
          :cy="pt[1]"
          r="8"
          fill="#1677ff"
        />
      </g>
      <line
        v-if="editable && draftPoints.length && cursorPoint"
        :x1="draftPoints[draftPoints.length - 1][0]"
        :y1="draftPoints[draftPoints.length - 1][1]"
        :x2="cursorPoint[0]"
        :y2="cursorPoint[1]"
        stroke="#1677ff"
        stroke-width="2"
        stroke-dasharray="4 4"
        pointer-events="none"
      />
    </svg>
    <div v-else class="map-empty">请先上传场地平面图</div>
  </div>
</template>

<script>
import { polygonCentroid, pointsToSvgString, countToHeatColor, getRegionDisplayName } from '../utils/venueMap';

export default {
  name: 'VenueMapCanvas',
  props: {
    layout: {
      type: Object,
      required: true,
    },
    counts: {
      type: Object,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: false,
    },
    selectedRegionIds: {
      type: Array,
      default: () => [],
    },
    selectedRegionId: {
      type: String,
      default: null,
    },
    draftPoints: {
      type: Array,
      default: () => [],
    },
    showCounts: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['region-click', 'canvas-click'],
  data() {
    return {
      cursorPoint: null,
    };
  },
  computed: {
    countRange() {
      const vals = Object.values(this.counts).filter(v => typeof v === 'number');
      if (!vals.length) return { min: 0, max: 100 };
      return { min: Math.min(...vals), max: Math.max(...vals) };
    },
    draftPointsSvg() {
      return pointsToSvgString(this.draftPoints);
    },
  },
  methods: {
    regionDisplayName(region) {
      return getRegionDisplayName(region);
    },
    regionPoints(region) {
      return pointsToSvgString(region.points || []);
    },
    labelPos(region) {
      return polygonCentroid(region.points || []);
    },
    isRegionSelected(region) {
      if (this.selectedRegionIds?.length) {
        return this.selectedRegionIds.includes(region.id);
      }
      return this.selectedRegionId === region.id;
    },
    regionFill(region) {
      if (this.isRegionSelected(region)) {
        return 'rgba(22, 119, 255, 0.45)';
      }
      const count = this.counts[region.id];
      if (count != null && this.showCounts) {
        const { min, max } = this.countRange;
        return countToHeatColor(count, min, max);
      }
      return 'rgba(145, 202, 255, 0.35)';
    },
    regionStroke(region) {
      if (this.isRegionSelected(region)) return '#0958d9';
      return 'rgba(0, 0, 0, 0.35)';
    },
    svgPointFromEvent(evt) {
      const svg = evt.currentTarget.closest('svg') || evt.currentTarget;
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return [0, 0];
      const transformed = pt.matrixTransform(ctm.inverse());
      return [Math.round(transformed.x), Math.round(transformed.y)];
    },
    onCanvasClick(evt) {
      if (!this.editable) return;
      const point = this.svgPointFromEvent(evt);
      this.$emit('canvas-click', point);
    },
    onMouseMove(evt) {
      if (!this.editable || !this.draftPoints.length) {
        this.cursorPoint = null;
        return;
      }
      this.cursorPoint = this.svgPointFromEvent(evt);
    },
    onRegionClick(region) {
      this.$emit('region-click', region);
    },
  },
};
</script>

<style scoped>
.venue-map-canvas {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d9d9d9;
}

.map-svg {
  width: 100%;
  height: auto;
  display: block;
  cursor: crosshair;
}

.region-polygon {
  cursor: pointer;
  transition: opacity 0.15s;
}

.region-polygon:hover {
  opacity: 0.9;
}

.region-label {
  font-size: 28px;
  font-weight: 600;
  fill: #1a1a1a;
  paint-order: stroke fill;
  stroke: #fff;
  stroke-width: 4px;
  white-space: pre;
}

.map-empty {
  padding: 80px 20px;
  text-align: center;
  color: #888;
}
</style>
